<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import propertyImage from 'dashboard/assets/real-estate/sales-overview-welcome.png';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  selectedId: { type: [Number, String], default: null },
});

const emit = defineEmits(['select', 'openDetails', 'openStatus']);

const numberValue = value => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string')
    return Number(value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
  if (value?.amount_cents) return Number(value.amount_cents) / 100;
  if (value?.cents) return Number(value.cents) / 100;
  return Number(value?.amount || 0);
};

const money = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(numberValue(value));

const fallbackRows = [
  {
    id: 'VDA-0217',
    processo_id: 217,
    parcela_id: 3,
    title: 'Apartamento 101 · Ed. Aurora',
    address: 'Rua Oscar Freire, 95 · Jardins',
    installment: '3 / 10',
    beneficiary: 'Imudei Incorporações',
    initials: 'IM',
    expected: '15/05/2025',
    amount: 12500,
    status: 'Previsto',
    tone: 'emerald',
  },
  {
    id: 'VDA-0198',
    processo_id: 198,
    parcela_id: 2,
    title: 'Casa 02 · Res. Jardim',
    address: 'Rua das Palmeiras, 250 · São Paulo',
    installment: '2 / 6',
    beneficiary: 'Carla Almeida',
    initials: 'CA',
    expected: '10/05/2025',
    amount: 8750,
    status: 'Atrasado',
    tone: 'red',
  },
  {
    id: 'VDA-0183',
    processo_id: 183,
    parcela_id: 1,
    title: 'Apto 502 · Ed. Verano',
    address: 'Av. das Acácias, 450 · Bela Vista',
    installment: '1 / 8',
    beneficiary: 'Imudei Incorporações',
    initials: 'IM',
    expected: '05/05/2025',
    amount: 15300,
    status: 'Pago',
    tone: 'emerald',
  },
  {
    id: 'VDA-0175',
    processo_id: 175,
    parcela_id: 4,
    title: 'Sala 1204 · Business Tower',
    address: 'Av. Paulista, 1200 · Bela Vista',
    installment: '4 / 12',
    beneficiary: 'João Silva',
    initials: 'JS',
    expected: '20/05/2025',
    amount: 6200,
    status: 'Previsto',
    tone: 'emerald',
  },
  {
    id: 'VDA-0169',
    processo_id: 169,
    parcela_id: 3,
    title: 'Cobertura 01 · Ed. Mirante',
    address: 'Rua Augusta, 801 · Consolação',
    installment: '3 / 5',
    beneficiary: 'Construtora Horizonte',
    initials: 'CH',
    expected: '18/05/2025',
    amount: 22300,
    status: 'Atrasado',
    tone: 'red',
  },
];

const statusTone = status => {
  const normalized = String(status || '').toLocaleLowerCase();
  if (normalized.includes('atras') || normalized.includes('venc')) return 'red';
  if (normalized.includes('pago') || normalized.includes('conclu'))
    return 'emerald';
  return 'amber';
};

const normalizeRow = row => ({
  id: row.id || `VDA-${row.processo_id || row.parcela_id}`,
  processo_id: row.processo_id || row.sales_transaction_id || row.id,
  parcela_id: row.parcela_id || row.installment_id || row.id,
  title:
    row.imovel?.titulo ||
    row.imovel?.nome ||
    row.endereco?.titulo ||
    row.logradouro ||
    'Venda vinculada',
  address:
    row.endereco?.logradouro ||
    [row.logradouro, row.numero, row.bairro].filter(Boolean).join(', ') ||
    'São Paulo · SP',
  installment: row.numero_parcela
    ? `${row.numero_parcela} / ${row.total_parcelas || '-'}`
    : '1 / 1',
  beneficiary:
    row.beneficiario?.nome ||
    row.pessoa?.nome ||
    row.cliente?.nome ||
    'Imudei Incorporações',
  initials: String(row.beneficiario?.nome || row.pessoa?.nome || 'IM')
    .split(' ')
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase(),
  expected:
    row.data_prevista || row.data_vencimento || row.vencimento || '15/05/2025',
  amount: row.valor_parcela || row.valor || 0,
  status: row.status_financeiro || row.status || 'Previsto',
  tone: statusTone(row.status_financeiro || row.status),
});

const displayRows = computed(() =>
  props.rows.length ? props.rows.map(normalizeRow) : fallbackRows
);
const activeId = computed(() => props.selectedId || displayRows.value[1]?.id);
const statusClass = tone =>
  ({
    emerald: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-700',
  })[tone] || 'bg-amber-50 text-amber-700';
</script>

<template>
  <main class="mx-auto w-full max-w-[1100px] px-4 pb-8 pt-3 sm:px-6 lg:px-5">
    <section
      class="mb-3 flex min-h-10 flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-100 bg-white px-4 py-2 text-[10px] text-[#334485] shadow-[0_3px_16px_rgba(58,43,104,0.05)]"
    >
      <strong class="text-[#172968]">Você pontua assim:</strong>
      <span class="rounded-lg bg-[#eff9f8] px-3 py-1.5"
        >Lançamento válido <b class="ml-2 text-[#059669]">+30</b></span
      >
      <span class="rounded-lg bg-[#eff9f8] px-3 py-1.5"
        >Conciliar <b class="ml-2 text-[#059669]">+50</b></span
      >
      <span class="rounded-lg bg-[#eff9f8] px-3 py-1.5"
        >Comprovante aprovado <b class="ml-2 text-[#059669]">+20</b></span
      >
      <span class="rounded-lg bg-[#eff9f8] px-3 py-1.5"
        >Fechamento em dia <b class="ml-2 text-[#059669]">+80</b></span
      >
    </section>
    <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="metric in [
          [
            'i-lucide-arrow-down',
            'A receber',
            'R$ 184.500',
            '24 parcelas',
            'text-[#0ea89c]',
            'bg-[#e8f8f6]',
            'bg-[#0ea89c]',
          ],
          [
            'i-lucide-arrow-up',
            'A pagar',
            'R$ 62.300',
            '11 parcelas',
            'text-[#f97316]',
            'bg-[#fff1e7]',
            'bg-[#f97316]',
          ],
          [
            'i-lucide-clock-3',
            'Vencido',
            'R$ 8.400',
            '6 parcelas',
            'text-[#ef4444]',
            'bg-[#ffe9ec]',
            'bg-[#ef4444]',
          ],
          [
            'i-lucide-check',
            'Pago no mês',
            'R$ 96.200',
            '18 parcelas',
            'text-[#159447]',
            'bg-[#eaf8ef]',
            'bg-[#159447]',
          ],
        ]"
        :key="metric[1]"
        class="flex h-[132px] flex-col rounded-2xl border border-slate-100 bg-white px-3.5 py-3 shadow-[0_3px_16px_rgba(58,43,104,0.06)]"
      >
        <div class="flex items-start gap-3">
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full"
            :class="[metric[4], metric[5]]"
          >
            <span :class="metric[0]" class="size-[18px]" />
          </span>
          <div class="min-w-0 flex-1">
            <p class="text-[11px] font-semibold text-[#202f75]">
              {{ metric[1] }}
            </p>
            <p
              class="mt-0.5 whitespace-nowrap text-[16px] font-bold"
              :class="metric[4]"
            >
              {{ metric[2] }}
            </p>
            <p class="mt-0.5 text-[10px] text-slate-500">{{ metric[3] }}</p>
          </div>
        </div>
        <div class="mt-auto flex items-end justify-between gap-2">
          <div class="flex -space-x-2">
            <span
              class="grid size-5 place-items-center rounded-full border-2 border-white bg-[#5933c5] text-[8px] font-bold text-white"
              >A</span
            >
            <span
              class="grid size-5 place-items-center rounded-full border-2 border-white bg-[#fed6c5] text-[8px] font-bold text-[#7c3d24]"
              >M</span
            >
            <span
              class="grid size-5 place-items-center rounded-full border-2 border-white bg-slate-100 text-[8px] font-bold text-slate-600"
              >+2</span
            >
          </div>
          <div class="flex h-7 items-end justify-end gap-1.5">
            <span class="h-1 w-1 rounded-full" :class="metric[6]" />
            <span class="h-3 w-1 rounded-full" :class="metric[6]" />
            <span class="h-2 w-1 rounded-full" :class="metric[6]" />
            <span class="h-5 w-1 rounded-full" :class="metric[6]" />
            <span class="h-3 w-1 rounded-full" :class="metric[6]" />
            <span class="h-7 w-1 rounded-full" :class="metric[6]" />
            <span class="h-4 w-1 rounded-full" :class="metric[6]" />
            <span class="h-5 w-1 rounded-full" :class="metric[6]" />
          </div>
        </div>
      </article>
    </section>

    <div class="mt-4 flex items-center gap-6 border-b border-slate-200 px-2">
      <button
        type="button"
        class="border-b-2 border-[#4922bb] px-1 pb-2.5 text-[12px] font-bold text-[#371395]"
      >
        Visão geral
      </button>
      <button
        type="button"
        class="px-1 pb-2.5 text-[12px] font-medium text-[#202f75]"
      >
        Recebimentos
      </button>
      <button
        type="button"
        class="px-1 pb-2.5 text-[12px] font-medium text-[#202f75]"
      >
        Pagamentos
      </button>
      <button
        type="button"
        class="px-1 pb-2.5 text-[12px] font-medium text-[#202f75]"
      >
        Comissões
      </button>
    </div>

    <section
      class="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)]"
    >
      <article
        class="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_3px_16px_rgba(58,43,104,0.06)]"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-[14px] font-bold text-[#172968]">
              Fluxo de caixa (próximos 6 meses)
            </h1>
            <div class="mt-3 flex flex-wrap gap-4 text-[11px] text-[#334485]">
              <span class="inline-flex items-center gap-1.5"
                ><i class="size-2 rounded-sm bg-[#0ea89c]" />A receber</span
              >
              <span class="inline-flex items-center gap-1.5"
                ><i class="size-2 rounded-sm bg-[#f97316]" />A pagar</span
              >
              <span class="inline-flex items-center gap-1.5"
                ><i class="size-2 rounded-sm bg-[#351b98]" />Saldo</span
              >
            </div>
          </div>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] font-medium text-[#334485]"
          >
            Próximos 6 meses <span class="i-lucide-chevron-down size-3" />
          </button>
        </div>
        <div class="mt-4 grid grid-cols-[48px_minmax(0,1fr)] gap-3">
          <div
            class="flex h-[150px] flex-col justify-between text-[10px] font-medium text-[#253674]"
          >
            <span>R$ 120.000</span><span>R$ 90.000</span><span>R$ 60.000</span
            ><span>R$ 30.000</span><span>R$ 0</span><span>-R$ 30.000</span>
          </div>
          <div class="relative">
            <div class="absolute inset-x-0 top-0 h-px bg-slate-200" />
            <div class="absolute inset-x-0 top-[30px] h-px bg-slate-100" />
            <div class="absolute inset-x-0 top-[60px] h-px bg-slate-100" />
            <div class="absolute inset-x-0 top-[90px] h-px bg-slate-100" />
            <div class="absolute inset-x-0 top-[120px] h-px bg-slate-100" />
            <div class="absolute inset-x-0 top-[150px] h-px bg-slate-200" />
            <div
              class="relative flex h-[173px] items-end justify-around gap-2 pt-2"
            >
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[62px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-8 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-11 w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Mai/25</span
                >
              </div>
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[78px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-7 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-[50px] w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Jun/25</span
                >
              </div>
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[82px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-10 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-[50px] w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Jul/25</span
                >
              </div>
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[58px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-7 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-[46px] w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Ago/25</span
                >
              </div>
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[50px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-6 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-9 w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Set/25</span
                >
              </div>
              <div
                class="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <div class="flex h-[142px] items-end gap-1">
                  <i class="h-[64px] w-3 rounded-t bg-[#0ea89c]" /><i
                    class="h-8 w-3 rounded-t bg-[#f97316]"
                  /><i class="h-[54px] w-3 rounded-t bg-[#351b98]" />
                </div>
                <span class="text-[10px] font-medium text-[#253674]"
                  >Out/25</span
                >
              </div>
            </div>
          </div>
        </div>
      </article>

      <article
        class="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_3px_16px_rgba(58,43,104,0.06)]"
      >
        <h2 class="text-[14px] font-bold text-[#172968]">
          Status das parcelas
        </h2>
        <div class="mt-3 flex items-center gap-4">
          <div
            class="relative grid size-40 place-items-center rounded-full bg-[conic-gradient(#0ea89c_0deg_223deg,#f59e0b_223deg_288deg,#ef4444_288deg_313deg,#ad8ae7_313deg_360deg)]"
          >
            <div
              class="grid size-20 place-items-center rounded-full bg-white text-center"
            >
              <span class="text-[10px] text-[#253674]">Total</span>
              <strong class="-mt-1 text-[22px] leading-none text-[#172968]"
                >230</strong
              >
              <span class="text-[10px] text-[#253674]">parcelas</span>
            </div>
          </div>
          <dl class="grid gap-3 text-[11px] text-[#253674]">
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2">
                <i class="size-2 rounded-full bg-[#0ea89c]" />Previsto
              </dt>
              <dd class="font-bold">143 (62%)</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2">
                <i class="size-2 rounded-full bg-[#f59e0b]" />Pago
              </dt>
              <dd class="font-bold">41 (18%)</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2">
                <i class="size-2 rounded-full bg-[#ef4444]" />Atrasado
              </dt>
              <dd class="font-bold">16 (7%)</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center gap-2">
                <i class="size-2 rounded-full bg-[#ad8ae7]" />Cancelado
              </dt>
              <dd class="font-bold">30 (13%)</dd>
            </div>
          </dl>
        </div>
      </article>
    </section>

    <section
      class="mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_3px_16px_rgba(58,43,104,0.06)]"
    >
      <div
        class="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3"
      >
        <label
          class="flex min-w-[215px] flex-1 items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] text-slate-400"
          ><span class="i-lucide-search size-4 text-[#334485]" /><input
            class="min-w-0 flex-1 border-0 bg-transparent p-0 text-[11px] outline-none"
            placeholder="Buscar por venda, favorecido ou nº da parcela"
        /></label>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] font-medium text-[#334485]"
        >
          <span class="i-lucide-calendar-days size-3.5" />Data prevista:
          01/05/2025 - 31/10/2025 <span class="i-lucide-chevron-down size-3" />
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] font-medium text-[#334485]"
        >
          Tipo: Todos <span class="i-lucide-chevron-down size-3" />
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] font-medium text-[#334485]"
        >
          Status: Todos <span class="i-lucide-chevron-down size-3" />
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-[11px] font-semibold text-[#334485]"
        >
          <span class="i-lucide-download size-3.5" />Exportar
        </button>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full min-w-[760px] text-left">
          <thead
            class="border-b border-slate-100 text-[10px] font-bold text-[#172968]"
          >
            <tr>
              <th class="px-4 py-3">Venda</th>
              <th class="px-3 py-3">Parcela</th>
              <th class="px-3 py-3">Favorecido</th>
              <th class="px-3 py-3">Previsto</th>
              <th class="px-3 py-3">Valor</th>
              <th class="px-3 py-3">Status</th>
              <th class="px-3 py-3">Ações</th>
            </tr>
          </thead>
          <tbody v-if="!loading" class="divide-y divide-slate-100">
            <tr
              v-for="row in displayRows"
              :key="row.id"
              class="cursor-pointer text-[11px] transition hover:bg-violet-50/50"
              :class="activeId === row.id ? 'bg-violet-50/70' : ''"
              @click="emit('select', row)"
            >
              <td class="px-4 py-2.5">
                <div class="flex items-center gap-2.5">
                  <img
                    :src="propertyImage"
                    alt=""
                    class="size-9 rounded-lg object-cover"
                  />
                  <div>
                    <strong class="block text-[11px] text-[#2840bb]">{{
                      row.id
                    }}</strong
                    ><span
                      class="block max-w-[136px] truncate text-[9px] text-[#45558c]"
                      >{{ row.title }}</span
                    >
                  </div>
                </div>
              </td>
              <td class="px-3 py-2.5 font-semibold text-[#172968]">
                {{ row.installment }}
              </td>
              <td class="px-3 py-2.5">
                <div class="flex items-center gap-2">
                  <span
                    class="grid size-7 place-items-center rounded-full bg-[#4c22ae] text-[9px] font-bold text-white"
                    >{{ row.initials }}</span
                  >
                  <div>
                    <strong class="block text-[10px] text-[#172968]">{{
                      row.beneficiary
                    }}</strong
                    ><span class="text-[9px] text-[#45558c]">Recebimento</span>
                  </div>
                </div>
              </td>
              <td class="px-3 py-2.5 font-medium text-[#172968]">
                {{ row.expected }}
              </td>
              <td class="px-3 py-2.5 font-bold text-[#172968]">
                {{ money(row.amount) }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="rounded-md px-2 py-1 text-[9px] font-bold"
                  :class="statusClass(row.tone)"
                  >{{ row.status }}</span
                >
              </td>
              <td class="px-3 py-2.5">
                <div class="flex gap-1">
                  <button
                    type="button"
                    class="grid size-7 place-items-center rounded-md border border-slate-200 text-[#334485]"
                    aria-label="Ver detalhes"
                    @click.stop="emit('openDetails', row)"
                  >
                    <span class="i-lucide-eye size-3.5" /></button
                  ><button
                    type="button"
                    class="grid size-7 place-items-center rounded-md border border-slate-200 text-[#334485]"
                    aria-label="Editar status"
                    @click.stop="emit('openStatus', row)"
                  >
                    <span class="i-lucide-pencil size-3.5" /></button
                  ><button
                    type="button"
                    class="grid size-7 place-items-center rounded-md border border-slate-200 text-[#048b80]"
                    aria-label="Conciliar"
                  >
                    <span class="i-lucide-square-check-big size-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td
                colspan="7"
                class="px-4 py-12 text-center text-sm text-slate-400"
              >
                Carregando lançamentos…
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <footer
        class="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-[10px] text-[#45558c]"
      >
        <span>Mostrando 1 a {{ displayRows.length }} de 32 parcelas</span>
        <div class="flex items-center gap-2">
          <span>Itens por página:</span
          ><button
            type="button"
            class="rounded-md border border-slate-200 px-3 py-1.5 text-[#172968]"
          >
            10
            <span
              class="i-lucide-chevron-down ml-1 inline-block size-3"
            /></button
          ><button
            type="button"
            class="grid size-7 place-items-center rounded-md border border-slate-200"
          >
            ‹</button
          ><button
            type="button"
            class="grid size-7 place-items-center rounded-md bg-[#421ba9] font-bold text-white"
          >
            1</button
          ><button
            type="button"
            class="grid size-7 place-items-center rounded-md border border-slate-200"
          >
            2</button
          ><button
            type="button"
            class="grid size-7 place-items-center rounded-md border border-slate-200"
          >
            3</button
          ><button
            type="button"
            class="grid size-7 place-items-center rounded-md border border-slate-200"
          >
            ›
          </button>
        </div>
      </footer>
    </section>
  </main>
</template>
